// src/components/ServiceTemplate/ServiceTemplate1.tsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Phone,
  Mail,
  Plus,
  X,
  Edit3,
  Check,
  Sparkles,
  Building2,
  Globe,
  MapPin,
  Upload,
  Crop,
  Image as ImageIcon,
  IdCard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageCropModal } from "@/components/ui/ImageCropModal";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import useServiceTemplateBase, {
  ServiceData,
  ServiceFlyerData,
} from "./ServiceTemplateBase";

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Copied
// verbatim from the property templates' own getEditableImageUrl helper.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

// ─── Helper: convert the crop modal's output data URL into a File, the same
// way a freshly-picked hero photo already becomes a File from a file input.
const dataUrlToFile = (dataUrl: string, filename: string): File => {
  const arr = dataUrl.split(",");
  const mimeMatch = arr[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
};

// =========================================================================
// DEFAULT VALUES
// =========================================================================
const DEFAULT_PROFILE = "https://via.placeholder.com/80/4B6BAA/ffffff?text=U";
const DEFAULT_COMPANY = "https://via.placeholder.com/80/4B6BAA/ffffff?text=C";
const DEFAULT_HERO_IMAGE =
  "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1738&q=80";

const MAX_FEATURES = 10;

type FeatureRow = {
  id: string;
  value: string;
};

const createFeatureId = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeFeatureValues = (features: any): string[] => {
  if (!Array.isArray(features)) return [];

  return features.map((feature) => {
    if (typeof feature === "string") return feature;
    if (typeof feature === "object" && feature !== null) {
      return feature.name || feature.feature_name || "";
    }
    return "";
  });
};

const mapFeatureRows = (
  features: any,
  previous: FeatureRow[] = []
): FeatureRow[] => {
  const normalized = normalizeFeatureValues(features);

  if (normalized.length === 0) return previous;
  if (
    previous.length === normalized.length &&
    previous.every((item, index) => item.value === normalized[index])
  ) {
    return previous;
  }

  const mapped = normalized.map((value, index) => ({
    id: previous[index]?.id ?? createFeatureId(),
    value,
  }));

  const trailingEmptyRows = previous
    .slice(normalized.length)
    .filter((row) => !row.value.trim());

  return [...mapped, ...trailingEmptyRows];
};

// =========================================================================
// TYPES
// =========================================================================
interface ServiceTemplate1Props {
  serviceData?: ServiceData;
  data?: ServiceData;
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Partial<ServiceFlyerData>) => void;
  readOnly?: boolean;
  isPreview?: boolean;
}

// =========================================================================
// MAIN COMPONENT
// =========================================================================
const ServiceTemplate1: React.FC<ServiceTemplate1Props> = ({
  serviceData: propServiceData,
  data: propData,
  onDirtyChange,
  onChange,
  readOnly = true,
  isPreview = false,
}) => {
  const serviceData = propServiceData || propData || {};

  const {
    flyerData,
    userData,
    loading,
    fileInputRef,
    handleInputChange,
    handleFileChange,
    removeHeroImage,
    saveFieldToBackend,
    toast,
  } = useServiceTemplateBase({
    serviceData,
    onDirtyChange,
    onChange,
  });

  // =========================================================================
  // INLINE EDITING STATES
  // =========================================================================
  const [editingFeatureId, setEditingFeatureId] = useState<string | null>(null);
  const [editingFeatureValue, setEditingFeatureValue] = useState<string>("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingServiceType, setIsEditingServiceType] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [featureRows, setFeatureRows] = useState<FeatureRow[]>(() =>
    mapFeatureRows(serviceData?.features || flyerData.features)
  );
  const heroContainerRef = useRef<HTMLDivElement | null>(null);
  const [boxHeroRes, setBoxHeroRes] = useState<string | null>(null);
  const [editingTitleValue, setEditingTitleValue] = useState("");
  const [editingDescriptionValue, setEditingDescriptionValue] = useState("");
  useEffect(() => {
    const measure = () => {
      if (!heroContainerRef.current) return;
      const width = Math.round(heroContainerRef.current.clientWidth);
      const height = Math.round(heroContainerRef.current.clientHeight);
      setBoxHeroRes(`${width}x${height}`);
    };

    measure();
    window.addEventListener("resize", measure);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined" && heroContainerRef.current) {
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(heroContainerRef.current);
    }

    return () => {
      window.removeEventListener("resize", measure);
      resizeObserver?.disconnect();
    };
  }, []);

  const handleHeroFileChangeWithResolution = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];

    if (file && boxHeroRes) {
      try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();
          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;
          tmp.src = dataUrl;
        });

        const parts = boxHeroRes
          .toLowerCase()
          .split("x")
          .map((s) => Number(s.trim()));

        if (parts.length === 2) {
          const [rw, rh] = parts;

          toast({
            title: "Resolution Suggestion",
            description: `Uploaded image is ${imgEl.naturalWidth}x${imgEl.naturalHeight}. Recommended: ${boxHeroRes}. If possible, upload this resolution for best results.`,
          });
        }
      } catch {
        // Ignore resolution-read failures and continue.
      }
    }

    handleFileChange(e, { onOriginalCaptured: setOriginalHeroImage });
  };

  // =========================================================================
  // EDIT IMAGE (crop/zoom the hero photo, non-destructively)
  // =========================================================================
  const EDIT_CROP_W = 257;
  const EDIT_CROP_H = 420; // proportional to the 380x620 hero box

  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });
  const [originalHeroImage, setOriginalHeroImage] = useState<string>("");

  const openEditImage = async () => {
    if (readOnly || !flyerData.heroImage) return;

    let originalSource = originalHeroImage;

    // This component may remount when the hero image changes, resetting local
    // state — so an empty originalHeroImage here doesn't necessarily mean
    // "never captured". Check the server (unaffected by remounts) before
    // assuming there isn't one and falling back to the current display.
    if (!originalSource && serviceData?.id) {
      try {
        const res = await axiosInstance.get(
          `${endpoints.service_listings}${serviceData.id}/`
        );
        originalSource = (res?.data as any)?.original_hero_image || "";
      } catch (err) {
        console.error("Failed to check for existing original hero image:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet. Self-heal from the current image so this
      // and every future edit has a stable, full source to re-crop from
      // instead of the current display.
      originalSource = flyerData.heroImage;
      if (serviceData?.id && !flyerData.heroImage.startsWith("data:")) {
        axiosInstance
          .patch(`${endpoints.service_listings}${serviceData.id}/`, {
            original_hero_image: flyerData.heroImage,
          })
          .catch((err) => {
            console.error("Failed to seed original hero image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    // Reuse the existing, unmodified hero upload handler by feeding it a
    // synthetic file-input change event, so the crop-confirm path saves
    // exactly like a normal re-upload (same toasts, same PATCH shape).
    const file = dataUrlToFile(croppedDataUrl, `edited-hero-${Date.now()}.png`);
    const syntheticEvent = {
      target: { files: [file], value: "" },
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    handleFileChange(syntheticEvent, { captureOriginal: false });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  useEffect(() => {
    setFeatureRows((prev) =>
      mapFeatureRows(serviceData?.features || flyerData.features, prev)
    );
  }, [serviceData?.id, serviceData?.features]);

  // =========================================================================
  // TITLE HANDLERS
  // =========================================================================
  const handleTitleClick = () => {
    if (readOnly) return;

    setEditingTitleValue(flyerData.title || "");
    setIsEditingTitle(true);
  };

  const handleTitleSave = () => {
    handleInputChange("title", editingTitleValue);
    void saveFieldToBackend("title", editingTitleValue);
    setIsEditingTitle(false);
  };

  const handleTitleCancel = () => {
    setEditingTitleValue("");
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleTitleSave();
    else if (e.key === "Escape") setIsEditingTitle(false);
  };

  // =========================================================================
  // SERVICE TYPE HANDLERS
  // =========================================================================
  const handleServiceTypeClick = () => {
    if (readOnly) return;
    setIsEditingServiceType(true);
  };

  const handleServiceTypeSave = () => setIsEditingServiceType(false);

  const handleServiceTypeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleServiceTypeSave();
    else if (e.key === "Escape") setIsEditingServiceType(false);
  };

  // =========================================================================
  // DESCRIPTION HANDLERS
  // =========================================================================
  const handleDescriptionClick = () => {
    if (readOnly) return;

    setEditingDescriptionValue(flyerData.description || "");
    setIsEditingDescription(true);
  };
  const handleDescriptionSave = () => {
    handleInputChange("description", editingDescriptionValue);
    void saveFieldToBackend("description", editingDescriptionValue);
    setIsEditingDescription(false);
  };

  const handleDescriptionCancel = () => {
    setEditingDescriptionValue("");
    setIsEditingDescription(false);
  };

  // =========================================================================
  // FEATURE INLINE EDIT HANDLERS
  // =========================================================================
  const handleEditFeature = (featureId: string) => {
    if (readOnly) return;
    const featureIndex = featureRows.findIndex((feature) => feature.id === featureId);
    if (featureIndex === -1) return;

    setEditingFeatureId(featureId);
    setEditingFeatureValue(featureRows[featureIndex]?.value || "");
  };

  const handleSaveFeature = (featureId: string) => {
    const featureIndex = featureRows.findIndex((feature) => feature.id === featureId);
    if (featureIndex === -1) return;

    const trimmedValue = editingFeatureValue.trim();
    const nextRows = trimmedValue
      ? featureRows.map((feature) =>
        feature.id === featureId ? { ...feature, value: trimmedValue } : feature
      )
      : featureRows.filter((feature) => feature.id !== featureId);

    const nextFeatures = nextRows
      .map((feature) => feature.value.trim())
      .filter((feature) => feature.length > 0);

    setFeatureRows(nextRows);
    handleInputChange("features", nextFeatures);
    void saveFieldToBackend("features", nextFeatures);
    setEditingFeatureId(null);
    setEditingFeatureValue("");
  };

  const handleCancelFeatureEdit = (featureId: string) => {
    const featureIndex = featureRows.findIndex((feature) => feature.id === featureId);
    if (featureIndex !== -1 && !featureRows[featureIndex]?.value) {
      setFeatureRows((prev) => prev.filter((feature) => feature.id !== featureId));
    }
    setEditingFeatureId(null);
    setEditingFeatureValue("");
  };

  const handleFeatureKeyDown = (e: React.KeyboardEvent, featureId: string) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveFeature(featureId);
    } else if (e.key === "Escape") {
      handleCancelFeatureEdit(featureId);
    }
  };

  const handleAddFeatureClick = () => {
    if (readOnly) return;

    // Count features that are actually displayed: non-empty OR currently being edited.
    const visibleCount = featureRows.filter(
      (feature) => feature.value !== "" || feature.id === editingFeatureId
    ).length;

    if (visibleCount >= MAX_FEATURES) {
      toast({
        title: "Feature Limit Reached",
        description: `You can add a maximum of ${MAX_FEATURES} features.`,
        variant: "destructive",
      });
      return;
    }

    const newFeatureId = createFeatureId();

    setFeatureRows((prev) => [...prev, { id: newFeatureId, value: "" }]);
    setEditingFeatureId(newFeatureId);
    setEditingFeatureValue("");
  };

  // =========================================================================
  // COMPUTED VALUES
  // =========================================================================
  const displayTitle =
    flyerData.title || flyerData.headline || "Your Service Title";
  const displayServiceType = flyerData.serviceType || "Service Type";
  const displayDescription =
    flyerData.description ||
    "Add a compelling description of your service here. Explain what you offer and why clients should choose you.";
  const heroImage = flyerData.heroImage || DEFAULT_HERO_IMAGE;
  const displayFeatures = featureRows.filter(
    (feature) => feature.value !== "" || feature.id === editingFeatureId
  );

  const agentFullName =
    flyerData.agentName || userData?.first_name
      ? `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim()
      : "Your Name";

  const displayPhone = flyerData.phone || flyerData.agentPhone || "";
  const displayEmail = flyerData.email || flyerData.agentEmail || "";
  const displayWebsite = flyerData.website || "";
  const displayAddress = flyerData.address || "";

  // =========================================================================
  // LOADING STATE
  // =========================================================================
  if (loading && !isPreview) {
    return (
      <div className="w-full min-h-screen bg-[#f5f5f0] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-[#7c9a7c] border-t-transparent rounded-full animate-spin" />
          <div className="text-[#4a5d4a] text-lg font-medium">
            Loading template...
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // RENDER
  // =========================================================================
  return (
    <div className="overflow-x-auto w-full" style={{ WebkitOverflowScrolling: "touch", transform: "rotateX(180deg)" }}>
      <div
        className="bg-[#f5f5f0] font-arial flex flex-col w-full"
        style={{ minWidth: "760px", transform: "rotateX(180deg)" }}
      >

        {/* ================================================================= */}
        {/* MAIN CONTENT - Two Column Layout                                   */}
        {/* FIX: Use `grid` with fixed row height on md+ so hero image never   */}
        {/* resizes when the right column content grows (features added).      */}
        {/* On mobile: stack vertically; hero is a fixed-height block.         */}
        {/* ================================================================= */}
        {/* <div className="flex flex-col md:grid md:grid-cols-2 md:items-start md:auto-rows-min"> */}
        <div className="flex flex-row items-start">

          {/* =============================================================== */}
          {/* LEFT COLUMN: Hero Image                                          */}
          {/* FIX: Use a wrapper that is always `position: relative` with a    */}
          {/* fixed height on mobile and `self-stretch` on desktop so the      */}
          {/* image column never collapses or shifts when features are added.  */}
          {/* =============================================================== */}
          <div
            ref={heroContainerRef}
            className="relative w-full group"
            style={{
              width: "380px",
              height: "620px",
              minWidth: "380px",
              minHeight: "620px",
              maxWidth: "380px",
              maxHeight: "620px",
            }}
          >
            {/* Inner absolutely-positioned container fills the column */}
            <div className="absolute inset-0 overflow-hidden">
              {!readOnly && boxHeroRes && (
                <div className="absolute bottom-3 left-3 z-30 pointer-events-none">
                  <div className="bg-black/80 text-white text-xs rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10 max-w-[320px]">
                    <div className="leading-snug">
                      <div className="font-semibold">
                        Required image resolution: {boxHeroRes}
                      </div>
                      <div className="mt-1 text-[11px]">
                        Please upload exactly this resolution.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {heroImage && heroImage !== DEFAULT_HERO_IMAGE ? (
                <>
                  <img
                    src={heroImage}
                    alt="Service Hero"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = DEFAULT_HERO_IMAGE;
                    }}
                  />

                  {/* Edit overlay on hover */}
                  {!readOnly && (
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-3 z-10">
                      <Button
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        Change Image
                      </Button>
                      <Button
                        size="sm"
                        onClick={openEditImage}
                        className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                      >
                        <Crop className="h-4 w-4 mr-2" />
                        Edit Image
                      </Button>
                      {/* <Button
                      size="sm"
                      onClick={removeHeroImage}
                      className="bg-red-500/80 backdrop-blur-sm text-white hover:bg-red-600"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Remove
                    </Button> */}
                    </div>
                  )}
                </>
              ) : (
                // Empty state - upload prompt
                <div
                  className={`absolute inset-0 flex flex-col items-center justify-center bg-[#7c9a7c]/10 ${!readOnly ? "cursor-pointer hover:bg-[#7c9a7c]/20" : ""
                    } transition-colors`}
                  onClick={() => !readOnly && fileInputRef.current?.click()}
                >
                  <ImageIcon className="h-16 w-16 sm:h-20 sm:w-20 text-[#7c9a7c] mb-4" />
                  <p className="text-xl sm:text-2xl font-light text-[#4a5d4a]">
                    {readOnly ? "No Image" : "Upload Service Photo"}
                  </p>
                  {!readOnly && (
                    <p className="text-sm text-[#7c9a7c] mt-2">
                      Click to select image
                    </p>
                  )}
                </div>
              )}

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleHeroFileChangeWithResolution}
            />

            {!readOnly && (
              <ImageCropModal
                open={cropModal.open}
                imageUrl={cropModal.imageUrl}
                title="Adjust Service Photo"
                outputSize={700}
                cropW={EDIT_CROP_W}
                cropH={EDIT_CROP_H}
                onConfirm={handleEditImageConfirm}
                onCancel={handleEditImageCancel}
              />
            )}

            {/*
            FIX for mobile stacking: a transparent placeholder that gives
            the column its natural height on mobile (the image fills via
            position:absolute into the parent's height).
            On md+ the grid row dictates height via items-stretch.
          */}
            <div className="invisible" style={{ minHeight: "620px" }} aria-hidden />
          </div>

          {/* =============================================================== */}
          {/* RIGHT COLUMN: Content                                            */}
          {/* =============================================================== */}
          <div
            className="bg-[#f5f5f0] px-5 sm:px-8 md:px-10 xl:px-12 py-6 sm:py-8 flex flex-col h-full min-h-[620px] flex-1"
          >
            <div className="w-full max-w-xl flex-1">

              {/* TITLE */}
              {!readOnly && isEditingTitle ? (
                <div className="flex items-start justify-between gap-3 mb-4 md:mb-6 w-full">
                  <input
                    type="text"
                    value={editingTitleValue}
                    onChange={(e) => setEditingTitleValue(e.target.value)}
                    onKeyDown={handleTitleKeyDown}
                    autoFocus
                    className="w-full min-w-0 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-[#4a5d4a] leading-[1.1] tracking-tight bg-transparent border-b-2 border-[#7c9a7c] focus:outline-none"
                    placeholder="Enter title..."
                  />

                  <div className="flex items-center gap-1 shrink-0 pt-2">
                    <button
                      onClick={handleTitleSave}
                      className="text-green-600 hover:text-green-700 p-1"
                    >
                      <Check className="w-5 h-5" />
                    </button>

                    <button
                      onClick={handleTitleCancel}
                      className="text-gray-500 hover:text-gray-700 p-1"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ) : (
                <h3
                  className={`text-xl sm:text-2xl md:text-2xl lg:text-3xl font-light text-[#4a5d4a] mb-4 md:mb-6 leading-[1.1] tracking-tight ${!readOnly
                    ? "cursor-pointer hover:text-[#5a7a5a] transition-colors group"
                    : ""
                    }`}
                  style={{
                    wordBreak: "break-word",
                  }}
                  onClick={handleTitleClick}
                  title={displayTitle}
                >
                  {displayTitle}
                  {!readOnly && (
                    <Edit3 className="inline-block ml-3 w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity text-[#7c9a7c]" />
                  )}
                </h3>
              )}

              {/* Divider */}
              <div className="w-full border-t border-gray-300 my-4 sm:my-6" />

              {/* DESCRIPTION */}
              {!readOnly && isEditingDescription ? (
                <div className="mb-6 sm:mb-8 md:mb-10">
                  <textarea
                    value={editingDescriptionValue}
                    onChange={(e) =>
                      setEditingDescriptionValue(e.target.value)
                    }
                    autoFocus
                    rows={4}
                    className="text-[#5a5a5a] text-sm sm:text-base leading-relaxed bg-white/50 border border-[#7c9a7c] rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-[#7c9a7c] w-full resize-none"
                    placeholder="Enter description..."
                  />

                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={handleDescriptionSave}
                      className="text-green-600 hover:text-green-700 p-1"
                    >
                      <Check className="w-5 h-5" />
                    </button>

                    <button
                      onClick={handleDescriptionCancel}
                      className="text-gray-500 hover:text-gray-700 p-1"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ) : (
                <p
                  className={`text-[#5a5a5a] text-sm sm:text-base leading-relaxed mb-6 sm:mb-8 md:mb-10 ${!readOnly
                    ? "cursor-pointer hover:bg-white/30 transition-colors rounded-lg p-2 -m-2 group"
                    : ""
                    }`}

                  onClick={handleDescriptionClick}

                >
                  {displayDescription}
                  {!readOnly && (
                    <Edit3 className="inline-block ml-2 w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-[#7c9a7c]" />
                  )}
                </p>
              )}

              {/* =========================================================== */}
              {/* KEY FEATURES SECTION                                         */}
              {/* =========================================================== */}
              <div className="mb-6 sm:mb-8 md:mb-10">

                {/* Features Header */}
                <div className="flex items-center justify-between mb-4 sm:mb-5">
                  <h4 className="text-xs uppercase tracking-[0.25em] text-[#7c9a7c] font-semibold">
                    Key Features
                  </h4>
                  {!readOnly && (
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${displayFeatures.length >= MAX_FEATURES
                        ? "bg-amber-100 text-amber-700"
                        : "bg-[#7c9a7c]/10 text-[#7c9a7c]"
                        }`}
                    >
                      {displayFeatures.length}/{MAX_FEATURES}
                    </span>
                  )}
                </div>

                {/* Features List */}
                {displayFeatures.length > 0 ? (
                  <ul className="space-y-1">
                    {displayFeatures.map(({ id, value: feature }) => {
                      const featureIndex = featureRows.findIndex((row) => row.id === id);
                      const isEditingFeature = editingFeatureId === id;

                      return (
                        <li
                          key={id}
                          className="flex items-center justify-between px-3 sm:px-4 py-2.5 rounded-md bg-[#f3f4ef] group"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            <span className="w-2 h-2 flex-shrink-0 rounded-full bg-[#7c9a7c]" />

                            {isEditingFeature ? (
                              <input
                                type="text"
                                value={editingFeatureValue}
                                onChange={(e) => setEditingFeatureValue(e.target.value)}
                                onKeyDown={(e) => handleFeatureKeyDown(e, id)}
                                autoFocus
                                maxLength={30}
                                placeholder="e.g., Deep Cleaning (Max 30 Chars)"
                                className="bg-transparent border-b border-[#7c9a7c] focus:outline-none text-sm text-[#4a5d4a] flex-1 min-w-0 w-full"
                              />
                            ) : (
                              <span
                                className={`text-sm text-[#4a5d4a] flex-1 truncate ${!readOnly ? "cursor-pointer hover:text-[#5a7a5a]" : ""
                                  }`}
                                onClick={() => handleEditFeature(id)}
                                title={!readOnly ? "Click to edit" : feature}
                              >
                                {feature}
                              </span>
                            )}
                          </div>

                          {!readOnly && (
                            <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                              {isEditingFeature ? (
                                <>
                                  <button
                                    onClick={() => handleSaveFeature(id)}
                                    className="text-green-600 hover:text-green-700 p-1"
                                    title="Save"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleCancelFeatureEdit(id)}
                                    className="text-gray-500 hover:text-gray-600 p-1"
                                    title="Cancel"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEditFeature(id)}
                                    className="text-[#7c9a7c] hover:text-[#5a7a5a] p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Edit"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                  {displayFeatures.filter((f) => (f.value || "").toString().trim().length > 0).length > 1 && (
                                    <button
                                      onClick={() => {
                                        if (featureIndex === -1) return;
                                        const nextRows = featureRows.filter((row) => row.id !== id);
                                        const nextFeatures = nextRows
                                          .map((feature) => feature.value.trim())
                                          .filter((feature) => feature.length > 0);
                                        setFeatureRows(nextRows);
                                        handleInputChange("features", nextFeatures);
                                        void saveFieldToBackend("features", nextFeatures);
                                        if (editingFeatureId === id) {
                                          setEditingFeatureId(null);
                                          setEditingFeatureValue("");
                                        }
                                      }}
                                      className="text-red-500 hover:text-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                      title="Delete"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="text-center py-8 bg-white/30 rounded-xl border-2 border-dashed border-[#7c9a7c]/30">
                    <Sparkles className="w-8 h-8 text-[#7c9a7c]/50 mx-auto mb-2" />
                    <p className="text-[#7c9a7c]/70 text-sm">
                      {readOnly
                        ? "No features added"
                        : "Click below to add your first feature"}
                    </p>
                  </div>
                )}

                {/* Add Feature Button */}
                {!readOnly && (
                  <button
                    onClick={handleAddFeatureClick}
                    disabled={displayFeatures.length >= MAX_FEATURES}
                    className={`
                    mt-4 w-full
                    min-h-[56px]
                    px-4
                    rounded-xl
                    border-2 border-dashed
                    flex items-center justify-center gap-2
                    text-sm font-medium
                    transition-all duration-200
                    ${displayFeatures.length >= MAX_FEATURES
                        ? "border-gray-300 text-gray-400 cursor-not-allowed bg-gray-50"
                        : "border-[#7c9a7c]/40 text-[#7c9a7c] hover:border-[#7c9a7c] hover:bg-[#7c9a7c]/5 hover:shadow-sm"
                      }
                  `}
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                    Add New Feature
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ================================================================= */}
        {/* FOOTER - Agent Info Section                                        */}
        {/* ================================================================= */}
        <div className="border-t border-[#7c9a7c]/20 bg-[#f5f5f0] p-3">
          <div className="max-w-6xl mx-auto">

            <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 sm:gap-6">

              {/* LEFT: Avatar */}
              <div className="flex-shrink-0 ">

                {/* Agent Avatar */}
                <div className="relative flex-shrink-0">
                  <div className="rounded-xl overflow-hidden  " style={{ width: "150px", height: "180px" }}>
                    <img
                      src={flyerData.agentAvatar || userData?.avatar || DEFAULT_PROFILE}
                      alt="Agent"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = DEFAULT_PROFILE;
                      }}
                    />
                  </div>
                  {/* <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" /> */}
                </div>
              </div>

              {/* CENTER: Agent Details */}
              <div className="min-w-0 text-center py-3">
                {/* Name */}
                {!readOnly ? (
                  <input
                    type="text"
                    value={flyerData.agentName}
                    onChange={(e) => handleInputChange("agentName", e.target.value)}
                    className="text-sm sm:text-base font-bold text-[#4a5d4a] bg-transparent border-none outline-none w-full mb-0.5 text-center"
                    placeholder="Your Name"
                  />
                ) : (
                  <p className="text-sm sm:text-base font-bold text-[#4a5d4a] truncate mb-0.5">
                    {agentFullName}
                  </p>
                )}

                {/* Contact rows */}
                <div className="flex flex-col items-center gap-1.5">

                  {/* Phone */}
                  {(displayPhone || !readOnly) && (
                    <div className="flex items-center justify-center gap-2 max-w-full">
                      <Phone className="w-4 h-4 text-[#9bb29b] flex-shrink-0" />

                      <span className="text-[18px] text-[#6b6b6b] truncate">
                        {displayPhone}
                      </span>
                    </div>
                  )}

                  {/* Email */}
                  {(displayEmail || !readOnly) && (
                    <div className="flex items-center justify-center gap-2 max-w-full">
                      <Mail className="w-4 h-4 text-[#9bb29b] flex-shrink-0" />

                      <span className="text-[18px] text-[#6b6b6b] truncate underline">
                        {/* {displayEmail} */}
                          Click here to email Me
                      </span>
                    </div>
                  )}

                  {/* Company */}
                  {(flyerData.company || userData?.company || !readOnly) && (
                    <div className="flex items-center justify-center gap-2 max-w-full">
                      <Building2 className="w-4 h-4 text-[#9bb29b] flex-shrink-0" />

                      <span className="text-[18px] text-[#6b6b6b] truncate">
                        {flyerData.company || userData?.company}
                      </span>
                    </div>
                  )}

                  {/* License Number */}
                  {(flyerData.license_number || userData?.license_number) && (
                    <div className="flex items-center justify-center gap-2 max-w-full">
                      <IdCard className="w-4 h-4 text-[#9bb29b] flex-shrink-0" />
                      <span className="text-[18px] text-[#6b6b6b] truncate">
                        License #: {flyerData.license_number || userData?.license_number}
                      </span>
                    </div>
                  )}

                </div>
              </div>


              {/* RIGHT: Company Logo */}
              <div className="flex-shrink-0">
                <div className="rounded-xl overflow-hidden  " style={{ width: "180px", height: "180px" }}>
                  <img
                    src={flyerData.companyLogo || userData?.company_logo || DEFAULT_COMPANY}
                    alt="Company Logo"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = DEFAULT_COMPANY;
                    }}
                  />
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceTemplate1;
